/**
 * Whitelabel branding — hostname-based logo/favicon overrides.
 *
 * Serving the app from a whitelabel domain (e.g. admin-odyssee.store-rentals.com)
 * replaces the Click&Share branding with the partner's admin branding. No DB
 * involved: the hostname IS the switch, and the branding is static config —
 * the partner's admin logo is deliberately distinct from the merchant's shop
 * logo (merchants.logo_url), which stays reserved for the customer-facing side.
 *
 * Assets live in public/whitelabel/ so they ship with the app (same origin,
 * versioned in git). Changing a partner's branding is an edit here + deploy.
 */

// Default (Click&Share) branding, used on all non-whitelabel hosts.
export const DEFAULT_LOGO_URL =
  'https://wtmlhsxgncbippkzqphg.supabase.co/storage/v1/object/public/products/product-images/click%20and%20share%20transparant.png';
export const DEFAULT_LOGO_ALT = 'Click & Share';

export interface WhitelabelBranding {
  logoUrl: string | null;
  /** null → neutral blank favicon (never the Click&Share one). */
  faviconUrl: string | null;
  /** Partner brand name, used for the logo alt text. */
  name: string;
}

const WHITELABEL_HOSTS: Record<string, WhitelabelBranding> = {
  'admin-odyssee.store-rentals.com': {
    logoUrl: '/whitelabel/odyssee-access.png',
    faviconUrl: null,
    name: 'Odyssée Access',
  },
};

/** Branding for the current host, or null on regular (Click&Share) domains. */
export function getWhitelabelBranding(
  hostname: string = window.location.hostname
): WhitelabelBranding | null {
  return WHITELABEL_HOSTS[hostname] ?? null;
}

// Blank favicon shown on whitelabel hosts when the partner has none set,
// so the Click&Share favicon from index.html never appears there.
const BLANK_FAVICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";

/**
 * Swap the favicon at runtime (index.html ships the Click&Share one).
 * Only called on whitelabel hosts, so a missing URL means "blank", never
 * "keep the Click&Share icon".
 */
export function applyFavicon(url: string | null): void {
  const href = url || BLANK_FAVICON;
  const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (link) {
    link.href = href;
  } else {
    const el = document.createElement('link');
    el.rel = 'icon';
    el.href = href;
    document.head.appendChild(el);
  }
}

/**
 * Apply whitelabel branding as early as possible (called from the app entry
 * point) so no page ever shows the Click&Share favicon on a partner domain.
 */
export function initWhitelabel(): void {
  const branding = getWhitelabelBranding();
  if (branding) applyFavicon(branding.faviconUrl);
}
